import React from 'react';
import { useResume } from '../context/ResumeContext';
import { ResumeSchemaService } from '../../../core/schema/ResumeSchemaService';
import EditorSectionHeader from '../components/EditorSectionHeader';

// Import all editor form components
import ContactSection from './ContactSection';
import SummarySection from './SummarySection';
import ExperienceSection from './ExperienceSection';
import ProjectSection from './ProjectSection';
import SkillsSection from './SkillsSection';
import EducationSection from './EducationSection';
import GenericListSection from './GenericListSection';
import InterestsSection from './InterestsSection';
import CustomSectionsForm from './CustomSectionsForm';

const EDITOR_COMPONENTS = {
  ContactEditor: ContactSection,
  SummaryEditor: SummarySection,
  ExperienceEditor: ExperienceSection,
  ProjectEditor: ProjectSection,
  SkillsEditor: SkillsSection,
  EducationEditor: EducationSection,
  GenericListEditor: GenericListSection,
  InterestsEditor: InterestsSection,
  CustomSectionEditor: CustomSectionsForm
};

export default function SectionRenderer({ section, isExpanded, onToggle, canMoveUp, canMoveDown }) {
  const { validationResults, activeResume, updateResumeState } = useResume();
  
  const results = validationResults[section.id] || { completed: false, percentage: 0, errors: [], warnings: [] };
  const settings = activeResume?.sectionSettings?.[section.id] || { enabled: true, visible: true, collapsed: false };
  
  const Component = EDITOR_COMPONENTS[section.editorComponent];
  const isCollapsible = section.layout?.collapsible !== false;

  const listItems = activeResume?.[section.id];
  const count = (section.allowMultiple && Array.isArray(listItems)) ? listItems.length : null;
  const displayTitle = count !== null ? `${section.title} (${count})` : section.title;

  const handleToggleVisibility = () => {
    const next = settings.visible
      ? ResumeSchemaService.hideSection(activeResume, section.id)
      : ResumeSchemaService.showSection(activeResume, section.id);
    updateResumeState(next);
  };

  const handleDisable = () => {
    if (window.confirm(`Are you sure you want to remove ${section.title}? Your data will be preserved but hidden.`)) {
      const next = ResumeSchemaService.disableSection(activeResume, section.id);
      updateResumeState(next);
    }
  };

  const handleMoveUp = () => {
    const next = ResumeSchemaService.moveSection(activeResume, section.id, 'up');
    updateResumeState(next, false); // Don't push to history for simple moves
  };

  const handleMoveDown = () => {
    const next = ResumeSchemaService.moveSection(activeResume, section.id, 'down');
    updateResumeState(next, false);
  };

  return (
    <div
      style={{
        background: 'var(--surface-1)',
        border: '1px solid var(--card-border)',
        borderRadius: '14px',
        overflow: (isExpanded || !isCollapsible) ? 'visible' : 'hidden',
        boxShadow: 'var(--shadow-sm)',
        transition: 'box-shadow 0.15s ease, background-color 0.15s ease',
        flexShrink: 0,
      }}
    >
      <EditorSectionHeader
        title={displayTitle}
        icon={section.icon}
        percentage={results.percentage}
        errors={results.errors}
        warnings={results.warnings}
        completed={results.completed}
        visible={settings.visible}
        isExpanded={isExpanded}
        isCollapsible={isCollapsible}
        canMoveUp={canMoveUp}
        canMoveDown={canMoveDown}
        onToggle={onToggle}
        onToggleVisibility={handleToggleVisibility}
        onDisable={handleDisable}
        onMoveUp={handleMoveUp}
        onMoveDown={handleMoveDown}
        capabilities={section.capabilities}
      />

      {(isExpanded || !isCollapsible) && Component && (
        <div
          style={{
            padding: '20px',
            borderTop: '1px solid var(--divider)',
            background: 'var(--surface-0)',
            borderBottomLeftRadius: '13px',
            borderBottomRightRadius: '13px',
          }}
        >
          {/* Support dynamic custom section form passing the active custom section ID */}
          <Component sectionId={section.id} />
        </div>
      )}
    </div>
  );
}
